import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExcalidrawCanvas } from 'atoms';
import type { IWhiteboard } from 'core';

import type {
  ExcalidrawImperativeAPI,
  ExcalidrawInitialDataState,
} from '@excalidraw/excalidraw/types/types';

import { useWhiteboardImages } from './useWhiteboardImages';

interface IWhiteboardViewer {
  board: IWhiteboard;
}

const EXCALIDRAW_LANG: Record<string, string> = { ru: 'ru-RU', pl: 'pl-PL' };

export const useExcalidrawLangCode = (): string => {
  const { i18n } = useTranslation();
  const short = (i18n.language || '').slice(0, 2);
  return EXCALIDRAW_LANG[short] || 'en';
};

/** Read-only board for students (after lesson end) and methodists. */
export const WhiteboardViewer = memo(({ board }: IWhiteboardViewer) => {
  const langCode = useExcalidrawLangCode();

  const apiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  const [apiReady, setApiReady] = useState(false);

  const setExcalidrawApi = useCallback((api: ExcalidrawImperativeAPI) => {
    apiRef.current = api;
    // Hydration needs both the imperative API and the resolved board query.
    setApiReady(true);
  }, []);

  // Hydration only (spec 0010 rev.3): viewers must SEE the S3 images, but
  // never upload. No whiteboardId is passed, so the upload half stays off and
  // the returned handler is deliberately unused — a view-mode canvas gets no
  // onChange anyway.
  useWhiteboardImages({ apiRef, apiReady, files: board.files });

  const initialData = useMemo<ExcalidrawInitialDataState | null>(
    () =>
      board.scene
        ? ({
            elements: board.scene.elements,
            appState: board.scene.appState,
            // Pre-rev.3 boards still carry their images inline in the scene —
            // keep honouring them; S3 hydration only adds files on top.
            files: board.scene.files,
            scrollToContent: true,
          } as unknown as ExcalidrawInitialDataState)
        : null,
    [board.scene],
  );

  return (
    <ExcalidrawCanvas
      initialData={initialData}
      viewModeEnabled
      excalidrawApiRef={setExcalidrawApi}
      langCode={langCode}
    />
  );
});
